// Every unit a player has fielded: decoding tools/buildUnitHistory.mjs's per-player file
// and filtering it for the profile's 🧩 Units tab. Pure (no fetching); the tab loads the
// file through dataStore.unitHistoryFor().
//
import { unitCombatStats } from './unitStats.js';
import { canonItem } from './itemAliases.js';

// Filters are chips, each {field, value}. Chips on different fields must all match. Chips
// on the same single-value field (two classes, two reactions) match either, since a unit
// has only one. Ability chips must all match: "Bio 3" + "Holy" means a unit with both.

export const COLORS = ['red', 'blue', 'green', 'yellow', 'white', 'black', 'purple', 'brown'];

// The file a canonKey is stored under. Twitch handles are [a-z0-9_], so this is almost
// always the key itself; anything else is hex-escaped so the name is filesystem-safe and
// two keys can't collide.
export const unitFile = (key) => key.replace(/[^a-z0-9_]/g, (c) => '-' + c.codePointAt(0).toString(16) + '-');

// The fields a chip can filter on, in picker order, with their labels.
export const FIELDS = [
  ['Class', 'Class'], ['Gender', 'Gender'], ['Sign', 'Zodiac'],
  ['ActionSkill', 'Secondary'], ['ReactionSkill', 'Reaction'], ['SupportSkill', 'Support'], ['MoveSkill', 'Move'],
  ['ability', 'Ability'],
  ['Mainhand', 'Weapon'], ['Offhand', 'Offhand'], ['Head', 'Head'], ['Armor', 'Body'], ['Accessory', 'Accessory'],
];
export const FIELD_LABEL = Object.fromEntries(FIELDS);
const SLOTS = ['ActionSkill', 'ReactionSkill', 'SupportSkill', 'MoveSkill', 'Mainhand', 'Offhand', 'Head', 'Armor', 'Accessory'];

// The preshow draft (2026-10) bans these outright.
export const DRAFT_BANNED = [['ActionSkill', 'Math Skill'], ['MoveSkill', 'Teleport 2']];
export const draftBanned = (u) => DRAFT_BANNED.filter(([f, v]) => u[f] === v).map(([, v]) => v);

// File → { name, key, units: [unit…] } oldest first. Each unit is shaped like the
// tournament API's (so the unit cards take it as is), plus `id` (the tournament), `color`,
// `wins` (bracket match wins, 0–3) and `title` (its team took the title).
export function decodeUnitHistory(file) {
  const s = file.s;
  let id = 0;
  const units = file.u.map((r) => {
    id += r[0];
    const u = {
      Name: file.n, Class: s[r[2]], Gender: s[r[3]], Sign: s[r[4]], Brave: r[5], Faith: r[6],
      ClassSkills: r[16].map((i) => s[i]), ExtraSkills: r[17].map((i) => s[i]),
      RaidBoss: !!(r[19] & 1),
      id, color: COLORS[r[1]], wins: r[18], title: !!(r[19] & 2),
    };
    SLOTS.forEach((f, j) => { u[f] = s[r[7 + j]]; });
    return u;
  });
  return { name: file.n, key: file.k, units };
}

const GEAR = new Set(['Mainhand', 'Offhand', 'Head', 'Armor', 'Accessory']);
// Gear is matched by its CURRENT name, so a filter on Short Blade also finds a Gladius.
const valuesOf = (u, field) => (field === 'ability' ? [...u.ClassSkills, ...u.ExtraSkills] : [GEAR.has(field) ? canonItem(u[field]) : u[field]]);

// Every value each field takes across these units, with how many units have it:
// [{field, value, n}], most common first within a field. Empty slots are left out.
export function facetValues(units) {
  const counts = new Map();
  for (const u of units) {
    for (const [field] of FIELDS) {
      for (const v of new Set(valuesOf(u, field))) {
        if (!v) continue;
        const k = field + '\u0000' + v;
        counts.set(k, (counts.get(k) || 0) + 1);
      }
    }
  }
  const out = [];
  for (const [k, n] of counts) { const [field, value] = k.split('\u0000'); out.push({ field, value, n }); }
  const order = Object.fromEntries(FIELDS.map(([f], i) => [f, i]));
  return out.sort((a, b) => order[a.field] - order[b.field] || b.n - a.n || a.value.localeCompare(b.value));
}

// The derived stats a filter or sort can use: class base + gear + move skill, as the
// unit cards compute them (lib/unitStats.js), with today's item data. Gear since removed
// from the game counts as nothing.
export const STAT_FIELDS = [['hp', 'HP'], ['mp', 'MP'], ['pa', 'PA'], ['ma', 'MA'], ['speed', 'Speed'], ['move', 'Move'], ['jump', 'Jump']];

// u → its stats, memoized per unit object. gd = {charStats, itemschanged, notAbles}.
export function statsLookup(gd) {
  const memo = new WeakMap();
  return (u) => {
    let s = memo.get(u);
    if (!s) { s = unitCombatStats(u, gd.charStats, gd.itemschanged, gd.notAbles).stats; memo.set(u, s); }
    return s;
  };
}

// opts: { chips:[{field,value}], brave:[min,max], faith:[min,max], titleOnly, raidOnly, hideBanned,
//         humanOnly, stats:{hp:[min|null, max|null], …}, statsOf(u) (needed when stats has bounds) }
export function filterUnits(units, opts = {}) {
  const byField = new Map();
  for (const c of opts.chips || []) {
    if (!byField.has(c.field)) byField.set(c.field, []);
    byField.get(c.field).push(c.value);
  }
  const [bLo, bHi] = opts.brave || [0, 100];
  const [fLo, fHi] = opts.faith || [0, 100];
  const statBounds = Object.entries(opts.stats || {}).filter(([, [lo, hi]]) => lo != null || hi != null);
  return units.filter((u) => {
    if (u.Brave < bLo || u.Brave > bHi || u.Faith < fLo || u.Faith > fHi) return false;
    if (opts.humanOnly && u.Gender === 'Monster') return false;
    if (statBounds.length) {
      const st = opts.statsOf(u);
      for (const [k, [lo, hi]] of statBounds) if ((lo != null && st[k] < lo) || (hi != null && st[k] > hi)) return false;
    }
    if (opts.titleOnly && !u.title) return false;
    if (opts.raidOnly && !u.RaidBoss) return false;
    if (opts.hideBanned && draftBanned(u).length) return false;
    for (const [field, vals] of byField) {
      if (field === 'ability') {
        const have = new Set(valuesOf(u, field));
        if (!vals.every((v) => have.has(v))) return false;
      } else if (!vals.includes(valuesOf(u, field)[0])) return false;
    }
    return true;
  });
}

export const SORTS = {
  newest: (a, b) => b.id - a.id,
  oldest: (a, b) => a.id - b.id,
  brave: (a, b) => b.Brave - a.Brave || b.id - a.id,
  faith: (a, b) => b.Faith - a.Faith || b.id - a.id,
};
export const STAT_SORTS = STAT_FIELDS.filter(([k]) => k !== 'mp' && k !== 'jump');   // offered in the Sort menu

// Sort in place by a SORTS key or a stat key (highest first; statsOf needed for those).
export function sortUnits(list, key, statsOf) {
  if (SORTS[key]) return list.sort(SORTS[key]);
  if (STAT_FIELDS.some(([k]) => k === key) && statsOf) return list.sort((a, b) => statsOf(b)[key] - statsOf(a)[key] || b.id - a.id);
  return list.sort(SORTS.newest);
}
export const isSortKey = (k) => !!SORTS[k] || STAT_FIELDS.some(([s]) => s === k);

// How far the unit's team got, in words.
export function resultLabel(u) {
  if (u.title) return 'Took the title';
  return ['Lost quarterfinal', 'Lost semifinal', 'Lost final', 'Lost title match'][u.wins] || '';
}

// Chips ↔ the URL: "Field:Value" joined by "|".
export const chipsToParam = (chips) => chips.map((c) => `${c.field}:${c.value}`).join('|');
export function chipsFromParam(p) {
  if (!p) return [];
  const known = new Set(FIELDS.map(([f]) => f));
  return p.split('|').map((x) => { const i = x.indexOf(':'); return { field: x.slice(0, i), value: x.slice(i + 1) }; })
    .filter((c) => known.has(c.field) && c.value);
}

// The filter bar's state ({chips, bLo, bHi, fLo, fHi, titleOnly, raidOnly, hideBanned, sort}),
// to and from URL params (uq, ub, uf, ut, ur, ux, us). Shared by the Units tab and ?submissions.
const range = (s) => { const m = /^(\d+)-(\d+)$/.exec(s || ''); return m ? [+m[1], +m[2]] : [0, 100]; };
// Stat bounds in the URL: ust=hp:300-,pa:10-14,speed:-8 (either end may be open).
const statsFromParam = (p) => {
  const out = {};
  for (const part of (p || '').split(',')) {
    const m = /^(\w+):(\d*)-(\d*)$/.exec(part);
    if (m && STAT_FIELDS.some(([k]) => k === m[1]) && (m[2] || m[3])) out[m[1]] = [m[2] ? +m[2] : null, m[3] ? +m[3] : null];
  }
  return out;
};
const statsToParam = (st) => Object.entries(st || {}).filter(([, [lo, hi]]) => lo != null || hi != null)
  .map(([k, [lo, hi]]) => `${k}:${lo ?? ''}-${hi ?? ''}`).join(',');

export function filtersFromParams(get, sortDefault = 'newest') {
  const [bLo, bHi] = range(get('ub')), [fLo, fHi] = range(get('uf'));
  return {
    chips: chipsFromParam(get('uq')), bLo, bHi, fLo, fHi,
    titleOnly: get('ut') === '1', raidOnly: get('ur') === '1', hideBanned: get('ux') === '1',
    humanOnly: get('uh') === '1', stats: statsFromParam(get('ust')),
    sort: get('us') || sortDefault,
  };
}
export function filtersToParams(f, sortDefault = 'newest') {
  const span = (lo, hi) => (lo || (hi ?? 100) !== 100 ? `${lo || 0}-${hi ?? 100}` : '');
  return {
    uq: chipsToParam(f.chips), ub: span(f.bLo, f.bHi), uf: span(f.fLo, f.fHi),
    ut: f.titleOnly ? '1' : '', ur: f.raidOnly ? '1' : '', ux: f.hideBanned ? '1' : '',
    uh: f.humanOnly ? '1' : '', ust: statsToParam(f.stats),
    us: f.sort === sortDefault ? '' : f.sort,
  };
}
export const filterOptions = (f, statsOf) => ({
  chips: f.chips, brave: [f.bLo || 0, f.bHi ?? 100], faith: [f.fLo || 0, f.fHi ?? 100],
  titleOnly: f.titleOnly, raidOnly: f.raidOnly, hideBanned: f.hideBanned,
  humanOnly: f.humanOnly, stats: f.stats || {}, statsOf,
});
